import EE from 'eventemitter3';
import { clickstreamLogger } from '~/logging';
import settings from '~/utils/Settings';

function getElementScroll(element) {
  const scroll = {
    top: element.scrollTop,
    left: element.scrollLeft,
  };

  if (element.nodeType === document.DOCUMENT_NODE) {
    scroll.top =
      typeof window.pageYOffset !== 'undefined'
        ? window.pageYOffset
        : (
          document.documentElement ||
          document.body.parentNode ||
          document.body
        ).scrollTop;
    scroll.left =
      typeof window.pageXOffset !== 'undefined'
        ? window.pageXOffset
        : (
          document.documentElement ||
          document.body.parentNode ||
          document.body
        ).scrollLeft;
  }

  return scroll;
}

function createEventObject(element, scroll) {
  const scrollStarted = element.currentScrollPosition
    ? element.currentScrollPosition
    : {
      top: 0,
      left: 0,
    };

  return {
    target: element,
    eventsCount: 1,
    timeStarted: Date.now(),
    timeFinished: Date.now(),
    scrollStarted,
    scrollFinished: scroll,
  };
}

function isSameDirection(previousEvent, scroll) {
  if (!previousEvent) {
    return false;
  }
  const topTotalDifference = scroll.top - previousEvent.scrollStarted.top;
  const topPreviousDifference =
    previousEvent.scrollFinished.top - previousEvent.scrollStarted.top;
  const leftTotalDifference = scroll.left - previousEvent.scrollStarted.left;
  const leftPreviousDifference =
    previousEvent.scrollFinished.left - previousEvent.scrollStarted.left;

  return (
    (Math.abs(topTotalDifference) >= Math.abs(topPreviousDifference) &&
      Math.abs(topTotalDifference) > 0) ||
    (Math.abs(leftTotalDifference) >= Math.abs(leftPreviousDifference) &&
      Math.abs(leftTotalDifference) > 0)
  );
}

export default class ScrollListener extends EE {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.emitPreviousEvent = this.emitPreviousEvent.bind(this);
    this.previousEvent = null;
    this.emitEventTimer = null;
  }

  get scrollEventDelay() {
    return settings.getOrDefault('SCROLL_EVENT_DELAY', 300);
  }

  emitPreviousEvent() {
    if (this.previousEvent === null) {
      return;
    }

    const e = this.previousEvent;
    const direction =
      e.scrollStarted.top > e.scrollFinished.top ? 'up' : 'down';

    clickstreamLogger.debug(
      'Element',
      e.target,
      `scrolled ${direction} position from (${e.scrollStarted.left}, ${e.scrollStarted.top}) to (${e.scrollFinished.left}, ${e.scrollFinished.top})`
    );
    this.emit('analytics', {
      type: 'scroll-state',
      target: e.target,
      eventsCount: e.eventsCount,
      scrollStarted: e.scrollStarted,
      scrollFinished: e.scrollFinished,
      direction,
      latency: e.timeFinished - e.timeStarted,
    });
    this.previousEvent = null;
    this.emitEventTimer = null;
  }

  onScroll(e) {
    if (!e || !e.target) {
      return;
    }
    if (this.emitEventTimer !== null) {
      clearTimeout(this.emitEventTimer);
    }
    const target = e.target;
    const scroll = getElementScroll(target);

    if (
      this.previousEvent &&
      this.previousEvent.target === target &&
      isSameDirection(this.previousEvent, scroll)
    ) {
      this.previousEvent.eventsCount++;
      this.previousEvent.timeFinished = Date.now();
      this.previousEvent.scrollFinished = scroll;
    } else {
      // Emits previous event only if this.previousEvent is not null
      this.emitPreviousEvent();
      this.previousEvent = createEventObject(target, scroll);
    }
    target.currentScrollPosition = scroll;
    this.emitEventTimer = setTimeout(
      this.emitPreviousEvent,
      this.scrollEventDelay
    );
  }

  start() {
    document.currentScrollPosition = getElementScroll(document);
    document.addEventListener('scroll', this.onScroll, true);
  }

  stop() {
    document.removeEventListener('scroll', this.onScroll, true);
  }
}
